import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CircularProgress from "./CircularProgress";
import styles from "./Countdown.module.css";

const Countdown = ({
	initialTimeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 },
	deadline,
	deadlineColor = "#008500",
	showDeadlineMissed = false,
	deadlineMissedTitle,
	deadlineMissedMessage,
}) => {
	const { t } = useTranslation();
	// State to track countdown time with safe default values
	const [timeLeft, setTimeLeft] = useState({
		days: initialTimeLeft?.days || 0,
		hours: initialTimeLeft?.hours || 0,
		minutes: initialTimeLeft?.minutes || 0,
		seconds: initialTimeLeft?.seconds || 0,
	});

	// Countdown effect - only run if not showing deadline missed state
	useEffect(() => {
		if (showDeadlineMissed) return;

		// Set up the countdown interval
		const timer = setInterval(() => {
			setTimeLeft((prevTime) => {
				// Calculate new time values
				let newSeconds = prevTime.seconds - 1;
				let newMinutes = prevTime.minutes;
				let newHours = prevTime.hours;
				let newDays = prevTime.days;

				// Handle time unit rollovers
				if (newSeconds < 0) {
					newSeconds = 59;
					newMinutes -= 1;
				}

				if (newMinutes < 0) {
					newMinutes = 59;
					newHours -= 1;
				}

				if (newHours < 0) {
					newHours = 23;
					newDays -= 1;
				}

				// Stop countdown if all values reach zero
				if (newDays < 0) {
					clearInterval(timer);
					return { days: 0, hours: 0, minutes: 0, seconds: 0 };
				}

				return { days: newDays, hours: newHours, minutes: newMinutes, seconds: newSeconds };
			});
		}, 1000);

		// Clean up the interval on component unmount
		return () => clearInterval(timer);
	}, [showDeadlineMissed]);

	// Render deadline missed state
	if (showDeadlineMissed) {
		return (
			<div className={styles.card}>
				<div className={styles.header}>
					<h3 className={styles.headerTitle}>{deadlineMissedTitle || t("predictions.countdown.matchweekDeadline")}</h3>
					<span className={styles.deadline} style={{ color: "#FF0000" }}>
						{deadline}
					</span>
				</div>
				<div className={styles.divider}></div>
				<div className={styles.content}>
					<div className={styles.progressContainer}>
						<CircularProgress value={0} maxValue={100} label={t("predictions.countdown.days")} size={65} />
						<CircularProgress value={0} maxValue={24} label={t("predictions.countdown.hours")} size={65} />
						<CircularProgress value={0} maxValue={60} label={t("predictions.countdown.minutes")} size={65} />
						<CircularProgress value={0} maxValue={60} label={t("predictions.countdown.seconds")} size={65} />
					</div>
					<div className={styles.deadlineMissedSection}>
						<h4 className={styles.deadlineMissedTitle}>{t("predictions.countdown.deadlineMissed")}</h4>
						<p className={styles.deadlineMissedMessage}>{deadlineMissedMessage || t("predictions.countdown.deadlineMissedMessage")}</p>
					</div>
				</div>
			</div>
		);
	}

	// Render normal countdown state
	return (
		<div className={styles.card}>
			<div className={styles.header}>
				<h3 className={styles.headerTitle}>{t("predictions.countdown.deadline")}</h3>
				<span className={styles.deadline} style={{ color: deadlineColor }}>
					{deadline}
				</span>
			</div>
			<div className={styles.divider}></div>
			<div className={styles.content}>
				<div className={styles.progressContainer}>
					<CircularProgress value={timeLeft.days} maxValue={100} label={t("predictions.countdown.days")} size={65} />
					<CircularProgress value={timeLeft.hours} maxValue={24} label={t("predictions.countdown.hours")} size={65} />
					<CircularProgress value={timeLeft.minutes} maxValue={60} label={t("predictions.countdown.minutes")} size={65} />
					<CircularProgress value={timeLeft.seconds} maxValue={60} label={t("predictions.countdown.seconds")} size={65} />
				</div>
			</div>
		</div>
	);
};

export default Countdown;
